import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import RegisterOpen from './RegisterOpen';
import RegisterClose from './RegisterClose';
import PayoutManagement from './PayoutManagement';

export default function RegisterManagement({ theme, user }) {
  const { t } = useLanguage();
  const [view, setView] = useState('dashboard'); // 'dashboard', 'open', 'close'
  const [registers, setRegisters] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showPayoutManagement, setShowPayoutManagement] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { sessionId, type: 'payouts'|'deposits', data: [] }
  const [sessionSortField, setSessionSortField] = useState('opened_at');
  const [sessionSortDirection, setSessionSortDirection] = useState('desc');
  const [searchTermSessions, setSearchTermSessions] = useState('');
  const [filterRegister, setFilterRegister] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [registersRes, sessionsRes, historyRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/registers`),
        fetch(`${BACKEND_URL}/api/registers/active`),
        fetch(`${BACKEND_URL}/api/reports/register-history?limit=200`)
      ]);

      if (registersRes.ok) {
        const regsData = await registersRes.json();
        setRegisters(regsData);
      }

      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setActiveSessions(sessionsData);
      }

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setPastSessions(historyData.sessions || historyData);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSuccess = (data) => {
    alert(`${t('registerOpened')}!\n${t('session')} ID: ${data.session_id}\n${t('openingCash')}: $${data.opening_cash}`);
    setView('dashboard');
    loadData();
  };

  const handleCloseSuccess = (data) => {
    const statusText = data.status === 'exact' ? t('exact') : data.status === 'over' ? t('over') : t('short');
    alert(
      `${t('registerClosed')}!\n\n` +
      `${t('expected')}: $${data.expected_cash.toFixed(2)}\n` +
      `${t('actual')}: $${data.actual_cash.toFixed(2)}\n` +
      `${t('difference')}: $${data.cash_difference.toFixed(2)}\n` +
      `${t('status')}: ${statusText}`
    );
    setView('dashboard');
    setSelectedSession(null);
    loadData();
  };

  const openRegistersCount = registers.filter(r => r.current_session).length;
  const closedRegistersCount = registers.length - openRegistersCount;

  const handleSessionSort = (field) => {
    if (sessionSortField === field) {
      setSessionSortDirection(sessionSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSessionSortField(field);
      setSessionSortDirection('asc');
    }
  };

  const showDetail = async (sessionId, type) => {
    try {
      if (type === 'payouts') {
        const res = await fetch(`${BACKEND_URL}/api/payouts/session/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setDetailModal({ sessionId, type, data });
        }
      } else {
        const res = await fetch(`${BACKEND_URL}/api/register-transactions/session/${sessionId}`);
        if (res.ok) {
          const allTx = await res.json();
          const deposits = allTx.filter(t => t.transaction_type === 'deposit');
          setDetailModal({ sessionId, type, data: deposits });
        }
      }
    } catch (err) {
      console.error('Error fetching details:', err);
    }
  };

  const fmtCurrency = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
  const fmtDate = (v) => v ? new Date(v).toLocaleString() : '-';

  // Filter past sessions
  let filteredSessions = pastSessions.filter(s => s.status !== 'open');
  if (filterRegister) {
    filteredSessions = filteredSessions.filter(s => s.register_id === parseInt(filterRegister));
  }
  const sessionsBeforeSearch = filteredSessions.length;
  if (searchTermSessions) {
    const search = searchTermSessions.toLowerCase();
    filteredSessions = filteredSessions.filter(s =>
      [s.register_name, s.opening_user_name, s.closing_user_name, s.status,
       s.session_id?.toString(), fmtCurrency(s.opening_cash), fmtCurrency(s.actual_cash)]
        .some(f => f && f.toString().toLowerCase().includes(search))
    );
  }
  // Sort
  filteredSessions = [...filteredSessions].sort((a, b) => {
    let aVal, bVal;
    switch (sessionSortField) {
      case 'opened_at': aVal = new Date(a.opened_at || 0); bVal = new Date(b.opened_at || 0); break;
      case 'closed_at': aVal = new Date(a.closed_at || 0); bVal = new Date(b.closed_at || 0); break;
      case 'register_name': aVal = (a.register_name || '').toLowerCase(); bVal = (b.register_name || '').toLowerCase(); break;
      case 'opening_user': aVal = (a.opening_user_name || '').toLowerCase(); bVal = (b.opening_user_name || '').toLowerCase(); break;
      case 'closing_user': aVal = (a.closing_user_name || '').toLowerCase(); bVal = (b.closing_user_name || '').toLowerCase(); break;
      case 'opening_cash': aVal = parseFloat(a.opening_cash) || 0; bVal = parseFloat(b.opening_cash) || 0; break;
      case 'expected_cash': aVal = parseFloat(a.expected_cash) || 0; bVal = parseFloat(b.expected_cash) || 0; break;
      case 'actual_cash': aVal = parseFloat(a.actual_cash) || 0; bVal = parseFloat(b.actual_cash) || 0; break;
      case 'cash_difference': aVal = parseFloat(a.cash_difference) || 0; bVal = parseFloat(b.cash_difference) || 0; break;
      case 'total_orders': aVal = parseInt(a.total_orders) || 0; bVal = parseInt(b.total_orders) || 0; break;
      case 'total_cash_sales': aVal = parseFloat(a.total_cash_sales) || 0; bVal = parseFloat(b.total_cash_sales) || 0; break;
      case 'total_payouts_amount': aVal = parseFloat(a.total_payouts_amount) || 0; bVal = parseFloat(b.total_payouts_amount) || 0; break;
      case 'total_deposits_amount': aVal = parseFloat(a.total_deposits_amount) || 0; bVal = parseFloat(b.total_deposits_amount) || 0; break;
      case 'session_id': aVal = a.session_id; bVal = b.session_id; break;
      default: aVal = a[sessionSortField]; bVal = b[sessionSortField];
    }
    if (aVal < bVal) return sessionSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sessionSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const sortArrow = (field) => sessionSortField === field ? (sessionSortDirection === 'asc' ? ' ▲' : ' ▼') : '';
  const thStyle = { padding: '0.75rem', textAlign: 'left', color: theme?.colors?.primary || '#1976d2', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const thStyleRight = { ...thStyle, textAlign: 'right' };
  const tdStyle = { padding: '0.75rem' };
  const tdStyleRight = { ...tdStyle, textAlign: 'right' };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        color: theme?.colors?.text || '#000'
      }}>
        {t('loading')}...
      </div>
    );
  }

  // Open Register View
  if (view === 'open') {
    return (
      <RegisterOpen
        theme={theme}
        user={user}
        registers={registers}
        onSuccess={handleOpenSuccess}
        onCancel={() => setView('dashboard')}
      />
    );
  }

  // Close Register View
  if (view === 'close' && selectedSession) {
    return (
      <RegisterClose
        theme={theme}
        user={user}
        session={selectedSession}
        onSuccess={handleCloseSuccess}
        onCancel={() => {
          setView('dashboard');
          setSelectedSession(null);
        }}
      />
    );
  }

  // Dashboard View
  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0rem'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          margin: 0,
          color: theme?.colors?.text || '#000',
          fontSize: '1.8rem',
          fontWeight: 600
        }}>
          {t('registerManagement')}
        </h1>

        <button
          onClick={() => setView('open')}
          disabled={closedRegistersCount === 0}
          style={{
            padding: '0.75rem .5rem',
            fontSize: '1rem',
            fontWeight: 500,
            border: 'none',
            borderRadius: '6px',
            backgroundColor: theme?.colors?.primary || '#1976d2',
            color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
            cursor: closedRegistersCount === 0 ? 'not-allowed' : 'pointer',
            opacity: closedRegistersCount === 0 ? 0.6 : 1
          }}
        >
          {t('openRegister')}
        </button>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(20px, 1fr))',
        gap: '0.5rem',
        marginBottom: '0rem'
      }}>
        <div style={{
          backgroundColor: theme?.colors?.surface || '#fff',
          padding: '.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: `2px solid ${theme?.colors?.success || '#4caf50'}`
        }}>
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('open')}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: theme?.colors?.success || '#4caf50' }}>
            {openRegistersCount}
          </div>
        </div>

        <div style={{
          backgroundColor: theme?.colors?.surface || '#fff',
          padding: '.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: `2px solid ${theme?.colors?.textSecondary || '#666'}`
        }}>
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('closed')}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: theme?.colors?.textSecondary || '#666' }}>
            {closedRegistersCount}
          </div>
        </div>

        <div style={{
          backgroundColor: theme?.colors?.surface || '#fff',
          padding: '.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: `2px solid ${theme?.colors?.primary || '#1976d2'}`
        }}>
          <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.5rem' }}>
            {t('total')}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: theme?.colors?.primary || '#1976d2' }}>
            {registers.length}
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '0rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          color: theme?.colors?.text || '#000',
          fontSize: '1.3rem',
          fontWeight: 600
        }}>
          {t('activeSessions')}
        </h2>

        {activeSessions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '0rem',
            color: theme?.colors?.textSecondary || '#666'
          }}>
            {t('noActiveSessions')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {activeSessions.map(session => (
              <div
                key={session.session_id}
                style={{
                  padding: '1.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: '8px',
                  backgroundColor: theme?.colors?.background || '#f5f5f5'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <h3 style={{
                      margin: '0 0 0.5rem 0',
                      color: theme?.colors?.text || '#000',
                      fontSize: '1.2rem',
                      fontWeight: 600
                    }}>
                      {session.register_name}
                    </h3>
                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('openedBy')}: {session.opening_user_name || session.opening_user_email} • {new Date(session.opened_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setSelectedSession(session);
                        setShowPayoutManagement(true);
                      }}
                      style={{
                        padding: '0.5rem 1.5rem',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        border: `1px solid ${theme?.colors?.primary || '#1976d2'}`,
                        borderRadius: '6px',
                        backgroundColor: theme?.colors?.surface || '#fff',
                        color: theme?.colors?.primary || '#1976d2',
                        cursor: 'pointer'
                      }}
                    >
                      💰 {t('payouts')}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSession(session);
                        setView('close');
                      }}
                      style={{
                        padding: '0.5rem 1.5rem',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: theme?.colors?.error || '#f44336',
                        color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      {t('closeRegister')}
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('openingCash')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.text || '#000' }}>
                      ${parseFloat(session.opening_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('cashSales')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
                      ${parseFloat(session.total_cash_sales || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('payouts')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.error || '#f44336' }}>
                      ${parseFloat(session.total_payouts || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('deposits')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
                      ${parseFloat(session.total_deposits || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('expectedCash')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.primary || '#1976d2' }}>
                      ${parseFloat(session.expected_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>
                      {t('orders')}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: theme?.colors?.text || '#000' }}>
                      {session.total_orders}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Registers List */}
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginTop: '2rem'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          color: theme?.colors?.text || '#000',
          fontSize: '1.3rem',
          fontWeight: 600
        }}>
          {t('allRegisters')}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {registers.map(register => (
            <div
              key={register.id}
              style={{
                padding: '1rem',
                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                borderRadius: '8px',
                backgroundColor: theme?.colors?.background || '#f5f5f5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: theme?.colors?.text || '#000',
                  marginBottom: '0.25rem'
                }}>
                  {register.name}
                </div>
                <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
                  {register.current_session ? (
                    `${t('open')} - ${t('session')} #${register.current_session.session_id}`
                  ) : (
                    t('closed')
                  )}
                </div>
              </div>
              <div style={{
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 600,
                backgroundColor: theme?.colors?.background || '#f5f5f5',
                color: register.current_session
                  ? theme?.colors?.success || '#4caf50'
                  : theme?.colors?.textSecondary || '#666'
              }}>
                {register.current_session ? t('open') : t('closed')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Session History */}
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginTop: '2rem'
      }}>
        <h2 style={{
          margin: '0 0 1rem 0',
          color: theme?.colors?.text || '#000',
          fontSize: '1.3rem',
          fontWeight: 600
        }}>
          {t('sessionHistory')}
        </h2>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', color: theme?.colors?.text || '#000' }}>{t('register')}:</span>
            <select
              value={filterRegister}
              onChange={(e) => setFilterRegister(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '6px',
                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                backgroundColor: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#000',
                fontSize: '0.9rem'
              }}
            >
              <option value="">{t('all')}</option>
              {registers.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder={t('searchSessions')}
            value={searchTermSessions}
            onChange={(e) => setSearchTermSessions(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000',
              fontSize: '0.9rem'
            }}
          />
          {searchTermSessions && (
            <span style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('showing')} {filteredSessions.length} {t('of')} {sessionsBeforeSearch}
            </span>
          )}
        </div>

        {/* Table */}
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {filteredSessions.length === 0 ? (
            <p style={{ color: theme?.colors?.textSecondary || '#666', textAlign: 'center', padding: '2rem' }}>
              {t('noClosedSessions')}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: theme?.colors?.surface || '#fff' }}>
                <tr style={{ borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}` }}>
                  <th onClick={() => handleSessionSort('session_id')} style={thStyle}>ID{sortArrow('session_id')}</th>
                  <th onClick={() => handleSessionSort('register_name')} style={thStyle}>{t('register')}{sortArrow('register_name')}</th>
                  <th onClick={() => handleSessionSort('opened_at')} style={thStyle}>{t('openedBy')}{sortArrow('opened_at')}</th>
                  <th onClick={() => handleSessionSort('closed_at')} style={thStyle}>{t('closedBy')}{sortArrow('closed_at')}</th>
                  <th onClick={() => handleSessionSort('opening_cash')} style={thStyleRight}>{t('openingCash')}{sortArrow('opening_cash')}</th>
                  <th onClick={() => handleSessionSort('total_cash_sales')} style={thStyleRight}>{t('cashSales')}{sortArrow('total_cash_sales')}</th>
                  <th onClick={() => handleSessionSort('total_payouts_amount')} style={thStyleRight}>{t('payouts')}{sortArrow('total_payouts_amount')}</th>
                  <th onClick={() => handleSessionSort('total_deposits_amount')} style={thStyleRight}>{t('deposits')}{sortArrow('total_deposits_amount')}</th>
                  <th onClick={() => handleSessionSort('expected_cash')} style={thStyleRight}>{t('expectedCash')}{sortArrow('expected_cash')}</th>
                  <th onClick={() => handleSessionSort('actual_cash')} style={thStyleRight}>{t('actualCash')}{sortArrow('actual_cash')}</th>
                  <th onClick={() => handleSessionSort('cash_difference')} style={thStyleRight}>{t('difference')}{sortArrow('cash_difference')}</th>
                  <th onClick={() => handleSessionSort('total_orders')} style={{ ...thStyle, textAlign: 'center' }}>{t('orders')}{sortArrow('total_orders')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((s, i) => {
                  const diff = parseFloat(s.cash_difference) || 0;
                  const diffColor = diff === 0
                    ? (theme?.colors?.success || '#4caf50')
                    : diff > 0
                    ? (theme?.colors?.warning || '#ff9800')
                    : (theme?.colors?.error || '#f44336');
                  return (
                    <tr key={s.session_id} style={{
                      backgroundColor: i % 2 === 0 ? theme?.colors?.surface : theme?.colors?.background,
                      borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`
                    }}>
                      <td style={tdStyle}>#{s.session_id}</td>
                      <td style={tdStyle}>{s.register_name}</td>
                      <td style={tdStyle}>
                        <div>{s.opening_user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>{fmtDate(s.opened_at)}</div>
                      </td>
                      <td style={tdStyle}>
                        <div>{s.closing_user_name || '-'}</div>
                        <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666' }}>{fmtDate(s.closed_at)}</div>
                      </td>
                      <td style={tdStyleRight}>{fmtCurrency(s.opening_cash)}</td>
                      <td style={{ ...tdStyleRight, color: theme?.colors?.success || '#4caf50' }}>{fmtCurrency(s.total_cash_sales)}</td>
                      <td style={{ ...tdStyleRight, color: theme?.colors?.error || '#f44336', cursor: parseFloat(s.total_payouts_amount) > 0 ? 'pointer' : 'default', textDecoration: parseFloat(s.total_payouts_amount) > 0 ? 'underline' : 'none' }}
                        onClick={() => parseFloat(s.total_payouts_amount) > 0 && showDetail(s.session_id, 'payouts')}>
                        {fmtCurrency(s.total_payouts_amount)}
                      </td>
                      <td style={{ ...tdStyleRight, color: theme?.colors?.success || '#4caf50', cursor: parseFloat(s.total_deposits_amount) > 0 ? 'pointer' : 'default', textDecoration: parseFloat(s.total_deposits_amount) > 0 ? 'underline' : 'none' }}
                        onClick={() => parseFloat(s.total_deposits_amount) > 0 && showDetail(s.session_id, 'deposits')}>
                        {fmtCurrency(s.total_deposits_amount)}
                      </td>
                      <td style={{ ...tdStyleRight, color: theme?.colors?.primary || '#1976d2' }}>{fmtCurrency(s.expected_cash)}</td>
                      <td style={tdStyleRight}>{fmtCurrency(s.actual_cash)}</td>
                      <td style={{ ...tdStyleRight, fontWeight: 600, color: diffColor }}>
                        {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{s.total_orders}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Payout/Deposit Detail Modal */}
      {detailModal && (
        <div
          onClick={() => setDetailModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme?.colors?.surface || '#fff',
              borderRadius: '12px', padding: '2rem', maxWidth: '700px',
              width: '90%', maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: theme?.colors?.text || '#000' }}>
                {detailModal.type === 'payouts' ? t('payouts') : t('deposits')} — {t('session')} #{detailModal.sessionId}
              </h3>
              <button onClick={() => setDetailModal(null)} style={{
                border: 'none', background: 'none', fontSize: '1.5rem',
                cursor: 'pointer', color: theme?.colors?.textSecondary || '#666'
              }}>×</button>
            </div>
            {detailModal.data.length === 0 ? (
              <p style={{ color: theme?.colors?.textSecondary || '#666', textAlign: 'center' }}>
                {t('noPayouts')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {detailModal.data.map((item, i) => (
                  <div key={item.payout_id || item.id || i} style={{
                    padding: '1rem', borderRadius: '8px',
                    border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                    backgroundColor: theme?.colors?.background || '#f5f5f5'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '1.1rem', color: detailModal.type === 'payouts' ? (theme?.colors?.error || '#f44336') : (theme?.colors?.success || '#4caf50') }}>
                        {detailModal.type === 'payouts' ? '-' : '+'}{fmtCurrency(item.amount)}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#666', padding: '0.25rem 0.5rem', borderRadius: '4px', backgroundColor: theme?.colors?.surface || '#fff' }}>
                        {item.payout_type || item.transaction_type || '-'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: theme?.colors?.text || '#000', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem' }}>
                      {item.recipient && <div>{t('recipient')}: {item.recipient}</div>}
                      {item.description && <div style={{ gridColumn: item.recipient ? 'auto' : '1 / -1' }}>{t('notes')}: {item.description}</div>}
                      <div>{t('createdBy')}: {item.created_by_email || '-'}</div>
                      {item.approved_by_email && <div>{t('approvedBy')}: {item.approved_by_email}</div>}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.25rem' }}>
                      {fmtDate(item.created_at || item.created)}
                      {item.approved_at && ` • ${t('approved')}: ${fmtDate(item.approved_at)}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payout Management Modal */}
      {showPayoutManagement && selectedSession && (
        <PayoutManagement
          theme={theme}
          user={user}
          session={selectedSession}
          onClose={() => {
            setShowPayoutManagement(false);
            setSelectedSession(null);
            loadData(); // Refresh data after closing
          }}
        />
      )}
    </div>
  );
}
